import type {
  Report,
  ReportCreateDto,
  ReportUpdateDto,
  ReportHistory,
  ReportSoTemplate,
  ReportSoTemplateDto,
  ReportResourceDto,
  ReportMultiExportPostDto,
  ResourceType,
} from "~/types/api";
import { BaseAPI } from "./base";
import type { ApiResponse } from "~/types/ui";

export class ReportApi extends BaseAPI {
  async getReports(): Promise<ApiResponse<Report[]>> {
    return this.get<Report[]>(`report/company/${this.company()}`);
  }

  async getReport(reportId: number): Promise<ApiResponse<Report>> {
    return this.get<Report>(`report/${reportId}`);
  }

  async createReport(payload: ReportCreateDto): Promise<ApiResponse<void>> {
    return this.post<void>(`report/${this.company()}`, payload);
  }

  async updateReport(
    reportId: number,
    payload: ReportUpdateDto,
  ): Promise<ApiResponse<void>> {
    return this.put<void>(`report/${reportId}`, payload);
  }

  async deleteReport(reportId: number): Promise<ApiResponse<void>> {
    return this.delete<void>(`report/${reportId}`);
  }

  async multiExport(
    payload: ReportMultiExportPostDto,
  ): Promise<ApiResponse<Blob>> {
    return this.postBlob(`report/multiexport/${this.company()}`, payload);
  }

  async manualExport(
    reportId: number,
    start: string,
    end: string,
  ): Promise<ApiResponse<void>> {
    return this.post<void>(`report/manual-export/${reportId}`, {
      start,
      end,
    });
  }

  async getTemplate(
    reportSoTemplateId: string,
  ): Promise<ApiResponse<ReportSoTemplate>> {
    return this.get<ReportSoTemplate>(`report/template/${reportSoTemplateId}`);
  }

  async getTemplates(): Promise<ApiResponse<ReportSoTemplate[]>> {
    return this.get<ReportSoTemplate[]>(`report/templates/${this.company()}`);
  }

  async createTemplate(
    payload: ReportSoTemplateDto,
  ): Promise<ApiResponse<ReportSoTemplate>> {
    return this.post<ReportSoTemplate>(
      `report/template/${this.company()}`,
      payload,
    );
  }

  async updateTemplate(
    reportSoTemplateId: string,
    payload: ReportSoTemplateDto,
  ): Promise<ApiResponse<void>> {
    return this.put<void>(`report/template/${reportSoTemplateId}`, payload);
  }

  async deleteTemplate(reportSoTemplateId: string): Promise<ApiResponse<void>> {
    return this.delete<void>(`report/template/${reportSoTemplateId}`);
  }

  async uploadTemplateFile(
    reportSoTemplateId: string,
    file: File,
  ): Promise<ApiResponse<void>> {
    const formData = new FormData();
    formData.append("file", file);
    return this.post<void>(
      `report/template/file/${reportSoTemplateId}`,
      formData,
    );
  }

  async getResources(
    resourceType: ResourceType,
  ): Promise<ApiResponse<ReportResourceDto[]>> {
    return this.get<ReportResourceDto[]>(
      `report/resources/${this.company()}/${resourceType}`,
    );
  }

  async getResourcesByResourceIds(
    ids: string[],
  ): Promise<ApiResponse<ReportResourceDto[]>> {
    return this.post<ReportResourceDto[]>(
      `report/resources/${this.company()}`,
      ids,
    );
  }

  // Company-scoped report generation history (the only mode used by the Report list's History tab)
  async getCompanyHistories(): Promise<ApiResponse<ReportHistory[]>> {
    return this.get<ReportHistory[]>(
      `report/history/company/${this.company()}`,
    );
  }

  async getUserHistories(): Promise<ApiResponse<ReportHistory[]>> {
    return this.get<ReportHistory[]>(`report/history/user/${this.user()}`);
  }

  async getReportHistories(
    reportId: number,
  ): Promise<ApiResponse<ReportHistory[]>> {
    return this.get<ReportHistory[]>(`report/history/report/${reportId}`);
  }

  async getReportHistoryFile(path: string): Promise<ApiResponse<Blob>> {
    return this.getBlob(`file/report-history/${path}`);
  }

  async getReportTemplateFile(path: string): Promise<ApiResponse<Blob>> {
    return this.getBlob(`file/report-template/${path}`);
  }
}
